import { useLogger } from '@/layers/presentation/hooks';
import {
  createProgram as createProgramService,
  fetchProgramById,
  updateProgram as updateProgramService,
} from '@/layers/presentation/services/programs';
import { Program } from '@/types/models';
import { NewProgramRequest } from '@/types/programs';
import { useCallback, useEffect, useState } from 'react';

const LOG_MODULE = 'FE [Presentation / Hooks / Program]';

export function useProgram(programId: number | null) {
  const [program, setProgram] = useState<Program | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const log = useLogger({
    module: LOG_MODULE,
    props: {
      programId,
    },
    data: {
      values: {
        program,
      },
      loading: {
        program: isLoading,
      },
      error,
    },
  });

  const fetchData = useCallback(async () => {
    if (programId === null) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const fetchedProgram = await fetchProgramById(programId);
      setProgram(fetchedProgram);
      setError(null);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateProgram = useCallback(
    async (updatedProgram: Partial<Program>) => {
      if (!program) return;

      try {
        log('Updating Program', { updatedProgram });
        const result = await updateProgramService(program.id.toString(), updatedProgram);
        setProgram(result);
        setError(null);
        return result;
      } catch (error) {
        setError((error as Error).message);
        throw error;
      }
    },
    [program, log],
  );

  const createProgram = useCallback(
    async (newProgram: NewProgramRequest) => {
      try {
        log('Creating Program', { newProgram });
        const created = await createProgramService(newProgram);
        setProgram(created);
        return created;
      } catch (error) {
        setError((error as Error).message);
        throw error;
      }
    },
    [log],
  );

  return {
    program,
    isLoading,
    error,
    updateProgram,
    createProgram,
    refetch: fetchData,
  };
}
